import { motion } from 'motion/react';
import { useState } from 'react';

interface BannerCardProps {
  id: string;
  text: string;
  ctaLabel: string;
  bannerImageUrl: string;
  bannerOverlayOpacity?: number;
  ctaLink?: string;
  variant?: 'card' | 'list';
  onDismiss?: (id: string) => void;
  isFirstInSection?: boolean;
}

export function BannerCard({
  id,
  text,
  ctaLabel,
  bannerImageUrl,
  bannerOverlayOpacity = 0.5,
  ctaLink,
  variant = 'card',
  onDismiss,
  isFirstInSection = false,
}: BannerCardProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleCTA = () => {
    // Just dismiss the card - no link opening
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onDismiss) onDismiss(id);
    }, 400);
  };

  const handleImageClick = () => {
    // Open link only when clicking the image
    if (ctaLink) {
      window.open(ctaLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Split text into title and subtitle (separated by double newline)
  const [title, subtitle] = text.split('\n\n');

  // List variant - horizontal inline banner with GetNinjas design
  if (variant === 'list') {
    if (!isVisible) return null;

    return (
      <motion.div
        key={`banner-${id}-list`}
        className="relative overflow-hidden"
        style={{
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: '#FCD34D',
          borderTopLeftRadius: isFirstInSection ? '16px' : '0',
          transition: 'border-radius 0.3s ease',
        }}
        initial={{ opacity: 0, height: 0 }}
        animate={
          isExiting
            ? { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }
            : { opacity: 1, height: 'auto' }
        }
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Clickable Image - Full Width */}
        <button
          onClick={handleImageClick}
          style={{
            width: '100%',
            height: '152px',
            border: 'none',
            padding: 0,
            cursor: ctaLink ? 'pointer' : 'default',
            background: 'none',
            display: 'block',
          }}
          aria-label="Abrir banner"
        >
          <img
            src={bannerImageUrl}
            alt="Banner"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        </button>

        {/* Footer - Dark Gray Bar */}
        <div
          style={{
            backgroundColor: '#374151',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          {/* CTA Button */}
          <motion.button
            onClick={handleCTA}
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'rgba(255, 255, 255, 0.9)',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              paddingLeft: '14px',
              paddingRight: '14px',
              paddingTop: '8px',
              paddingBottom: '8px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: '36px',
            }}
            whileTap={{ scale: 0.96 }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
            }}
          >
            {ctaLabel}
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // Card variant - GetNinjas design with yellow background
  if (!isVisible) return null;

  return (
    <motion.div
      className="overflow-hidden"
      style={{
        borderRadius: 'var(--radius-card)',
        backgroundColor: '#FCD34D',
      }}
      initial={{ opacity: 0, x: -20 }}
      animate={
        isExiting
          ? { opacity: 0, scale: 0.95, height: 0, marginBottom: 0 }
          : { opacity: 1, x: 0 }
      }
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Clickable Image - Full Width */}
      <button
        onClick={handleImageClick}
        style={{
          width: '100%',
          height: '152px',
          border: 'none',
          padding: 0,
          cursor: ctaLink ? 'pointer' : 'default',
          background: 'none',
          display: 'block',
        }}
        aria-label="Abrir banner"
      >
        <img
          src={bannerImageUrl}
          alt="Banner"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            borderTopLeftRadius: 'var(--radius-card)',
            borderTopRightRadius: 'var(--radius-card)',
          }}
        />
      </button>

      {/* Footer - Dark Gray Bar */}
      <div
        style={{
          backgroundColor: '#374151',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingLeft: '20px',
          paddingRight: '20px',
          borderBottomLeftRadius: 'var(--radius-card)',
          borderBottomRightRadius: 'var(--radius-card)',
        }}
      >
        {/* CTA Button */}
        <motion.button
          onClick={handleCTA}
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'rgba(255, 255, 255, 0.9)',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingTop: '6px',
            paddingBottom: '6px',
            borderRadius: 'var(--radius-button)',
            minHeight: '32px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          whileTap={{ scale: 0.96 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
          }}
          aria-label={ctaLabel}
        >
          {ctaLabel}
        </motion.button>
      </div>
    </motion.div>
  );
}